import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { IoTrashOutline } from 'react-icons/io5';
import { motion, AnimatePresence } from 'motion/react';
import {
  AiComponentHtmlAttribute,
  AiComponentJsVariable,
  AiManifestComponent,
} from '@/pkg/ai';
import { AiComponentGenericInput } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/generic-input';
import { Tooltip } from '@/components/tooltip';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { EditorContext } from '@/features/editor/context/editor-context';
import { DeviceType } from '@/utils/definitions';
import { toGroupName } from '@/features/editor/widgets/shared/ai-editing-layout/utils/group-name';

export interface AiComponentInputCardProps {
  component: AiManifestComponent;
  removeGroupName?: boolean;
  expandedAsDefault?: boolean;
  hideSeparators?: boolean;
  isFirstInGroup?: boolean;
}

export function AiComponentInputCard({
  component,
  removeGroupName,
  expandedAsDefault,
  hideSeparators,
  isFirstInGroup: isFirstInGroupProp,
}: AiComponentInputCardProps) {
  const {
    devicePreview: {
      editorState: { device },
    },
  } = useContext(EditorContext);
  const [isExpanded, setIsExpanded] = React.useState(expandedAsDefault);
  const getAttribute = useAiComponentContext((ctx) => ctx.getAttribute);
  const setAttribute = useAiComponentContext((ctx) => ctx.setAttribute);
  const getCssRule = useAiComponentContext((ctx) => ctx.getCssRule);
  const getJsVariables = useAiComponentContext(
    (ctx) => ctx.getManifestVariables,
  );
  const getTextContent = useAiComponentContext((ctx) => ctx.getTextContent);
  const getVideoAttributes = useAiComponentContext(
    (ctx) => ctx.getVideoAttributes,
  );
  const isFirstInGroupContext = useAiComponentContext(
    (ctx) => ctx.isFirstInGroup,
  );

  const isFirstInGroup =
    isFirstInGroupProp !== undefined
      ? isFirstInGroupProp
      : isFirstInGroupContext(component);

  React.useEffect(() => {
    const handler = (ev: any) => {
      if (ev.detail.group === component.group) {
        setIsExpanded(true);
      }
    };
    window.addEventListener(`ai-expand-block`, handler);
    return () => window.removeEventListener(`ai-expand-block`, handler);
  }, [component.group]);

  const toggleExpand = () => {
    if (!isRemoved) {
      setIsExpanded((prev) => !prev);
    }
  };

  const canBeRemoved = useMemo(
    () => component.type === `html-content`,
    [component],
  );

  const mockDisplayComponent = useMemo(
    () =>
      ({
        attr: `hidden`,
        type: `html-attr`,
        node: ``,
        value: ``,
        name: ``,
        description: ``,
        selector: component.selector,
      } as AiComponentHtmlAttribute),
    [component],
  );

  const [isRemoved, setIsRemoved] = React.useState(
    canBeRemoved &&
      getAttribute(component.selector, mockDisplayComponent) === `true`,
  );

  const onToggleVisibility = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (canBeRemoved) {
      setIsRemoved((prev) => !prev);
      setIsExpanded(isRemoved);
      setAttribute(
        component.selector,
        mockDisplayComponent,
        isRemoved ? `` : `true`,
      );
    }
  };

  const groupClassName = useMemo(
    () => toGroupName(component.group),
    [component],
  );

  const formattedTitle = useMemo(() => {
    let { name } = component;
    if (component.group && removeGroupName) {
      name = name.split(component.group).join(``);
    }

    const words = name.trim().split(/\s+/);
    const resultWords: string[] = [];
    for (let i = 0; i < words.length; i += 1) {
      if (i === 0 || words[i].toLowerCase() !== words[i - 1].toLowerCase()) {
        resultWords.push(words[i]);
      }
    }

    const finalTitle = resultWords.join(` `).trim();
    return finalTitle || component.name;
  }, [component.name, component.group, removeGroupName]);

  const valueHint = useMemo(() => {
    try {
      if (component.type === `html-content`) {
        const text = getTextContent(component.selector);
        return text?.trim() || `No content`;
      }
      if (component.type === `html-attr`) {
        return getAttribute(component.selector, component);
      }
      if (component.type === `js-variable`) {
        const jsVar = component as AiComponentJsVariable;
        const vars = getJsVariables();
        const v = vars.find((x) => x.variable === jsVar.variable);
        return String(v?.value ?? jsVar.value);
      }
      if (component.type === `css-rule`) {
        const rules = getCssRule(component.selector);
        return rules?.declarations?.map((d) => d.name).join(`, `);
      }
      if (component.type === `html-video`) {
        const video = getVideoAttributes(component.selector);
        return video?.source || `No video source`;
      }
    } catch (e) {
      console.error(`Error calculating hint`, e);
    }
    return ``;
  }, [
    component,
    getAttribute,
    getCssRule,
    getJsVariables,
    getTextContent,
    getVideoAttributes,
  ]);

  return (
    <>
      {isFirstInGroup && (
        <GroupSection>
          {!hideSeparators && <Seperator />}
          <GroupName
            layout
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`vsly-group-${groupClassName}`}
          >
            {component.group}
          </GroupName>
        </GroupSection>
      )}
      <Wrapper
        layout
        isRemoved={isRemoved}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        className={device === DeviceType.Desktop ? `desktop` : ``}
      >
        <TitleWrapper layout isRemoved={isRemoved} onClick={toggleExpand}>
          <TitleContainer>
            <Title
              className={`vsly-comp-selector-${toGroupName(
                component?.selector,
              )}`}
            >
              {formattedTitle}
              {component.description && (
                <Tooltip title={component.name} text={component.description} />
              )}
            </Title>
            {valueHint && (
              <Hint className={isExpanded ? `hide` : `show`}>{valueHint}</Hint>
            )}
          </TitleContainer>
          <ControlsWrapper layout className={isExpanded ? `expanded` : ``}>
            {canBeRemoved && (
              <DisableButton
                layout
                isRemoved={isRemoved}
                onClick={onToggleVisibility}
              >
                <IoTrashOutline size={18} color="inherit" />
              </DisableButton>
            )}
            {!isRemoved && (
              <motion.div layout>
                {isExpanded ? <FaMinus /> : <FaPlus />}
              </motion.div>
            )}
          </ControlsWrapper>
        </TitleWrapper>
        <AnimatePresence>
          {isExpanded && (
            <ContentWrapper
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: `auto`, opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: `linear` }}
            >
              <AiComponentGenericInput component={component} />
            </ContentWrapper>
          )}
        </AnimatePresence>
      </Wrapper>
    </>
  );
}

const Wrapper = styled(motion.div)<{ isRemoved?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: rgba(177, 217, 203, 0.18) 0px 5px 26px 0px;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);

  ${({ isRemoved }) =>
    isRemoved &&
    `
    opacity: 0.4!important;
  `}

  &&:has(.ai-component-generic-input:empty) {
    display: none;
  }

  &&.desktop {
    padding: 1.5rem;

    div[class*='custom-toolbar-rich-text'] {
      gap: 0.5rem;
    }
  }
`;

const TitleWrapper = styled(motion.div)<{ isRemoved?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  color: #707a87;
  cursor: ${({ isRemoved }) => (isRemoved ? `default` : `pointer`)};

  &:hover {
  }

  &:active {
    opacity: 1;
  }

  user-select: none;
  * {
    user-select: none;
  }
`;

const Title = styled(motion.span)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  letter-spacing: -0.17px;
  color: #313b47;
  font-size: 1.2rem;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, serif;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Hint = styled(motion.div)`
  font-size: 1.2rem;
  font-weight: 400;
  margin-top: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
  letter-spacing: -0.16px;
  color: #9da9b7;
  transition: opacity 0.2s linear;

  &&.show {
    opacity: 1;
  }

  &&.hide {
    opacity: 0;
  }
`;

const GroupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  user-select: none;
  && * {
    user-select: none;
  }
`;

const GroupName = styled(motion.div)`
  && {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-weight: 600;
    letter-spacing: -0.17px;
    color: #656f7b;
    font-size: 1.3rem;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      Helvetica, serif;
    margin-left: 1.4rem;

    transition: color 0.3s ease-in-out;
  }
`;

const Seperator = styled.span`
  width: 100%;
  height: 1px;
  background-color: rgba(224, 224, 224, 0.54);
  display: block;
`;

const ControlsWrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;

  &&.expanded {
    transform: translate(0, -1.3rem);
  }

  transition: transform 0.2s ease-in-out;
`;

const DisableButton = styled(motion.button)<{ isRemoved?: boolean }>`
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c8c9a;
  position: relative;

  ${({ isRemoved }) =>
    isRemoved &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 1.8rem;
      height: 1.5px;
      background-color: currentColor;
      transform: translate(-50%, -50%) rotate(-45deg);
    }
  `}

  &:hover {
    opacity: ${({ isRemoved }) => (isRemoved ? 1 : 0.8)};
  }
  transition: opacity 0.2s ease-in-out;
`;

const ContentWrapper = styled(motion.div)`
  overflow: visible;
`;
